import { useMutation } from '@tanstack/react-query'
import { DocumentInterface, ProgressEventHandler, UploadStore } from 'features/Documents'
import { useState } from 'react'
import { useRootStore } from 'service/useRootStore'
import { logError } from 'utils/logger'

export interface DocumentInformation extends DocumentInterface {
  fileInstance: File
  documentTypeCode: string
  source: 'customer'
}

type DocumentState = 'published' | 'unpublished' | 'deleted'
type DocumentSource = 'customer' | 'operations' | 'finance'

type ManualQuoteDocumentUploadJson = {
  state: DocumentState
  mimeType: string
  customerID: string
  source: DocumentSource
  documentTypeCode?: string
  documentTypeAliasName?: string
  documentTypeAliasCode?: string
}

export type ManualQuoteDocumentUploadVariables = {
  manualQuoteID: string
  file: File
  json: ManualQuoteDocumentUploadJson
  onProgress?: ProgressEventHandler
  documentID?: string
}

export const useManualQuoteDocumentsApi = () => {
  const { uiStore: ui, API, authStore } = useRootStore()
  const uploadStore = new UploadStore(authStore, API)
  const [localFileCollection, setLocalFileCollection] = useState<DocumentInformation[]>([])

  const handleNonStandardErrors = (e: unknown) => {
    if (e instanceof Error) {
      if ('message' in e) {
        ui.snack.notifyError(e.message)
      }
      logError(e as Error)
    }
  }

  const createObjectURL = async (file: File, mimeType: string): Promise<string> => {
    try {
      return URL.createObjectURL(new Blob([file], { type: mimeType }))
    } catch (error) {
      handleNonStandardErrors(error)
      return ''
    }
  }

  const documentInformationMapper = (file: File, customerID: string, documentType: string): DocumentInformation => {
    const _id = `${file.name}-${file.size}-${Date.now()}`
    return {
      role: documentType,
      name: file.name,
      userProvided: true,
      _id,
      fileInstance: file,
      documentTypeCode: documentType,
      source: 'customer',
      customer_id: customerID,
      createdAt: new Date(),
      modifiedAt: new Date(),
      mimeType: file.type,
      file: file.name,
    }
  }

  return {
    updateLocalFileCollection: (
      file: File,
      documentType: string,
      customerID: string,
      _onProgressHandler: ProgressEventHandler
    ): Promise<DocumentInformation[]> => {
      return new Promise((resolve) => {
        const updatedFileCollection = [
          ...localFileCollection,
          documentInformationMapper(file, customerID, documentType),
        ]
        setLocalFileCollection(() => updatedFileCollection)
        resolve(updatedFileCollection)
      })
    },
    createPreviewURL: (file: File, mimeType: string) => createObjectURL(file, mimeType),
    getLocalDocuments: (): Array<DocumentInformation> => {
      return localFileCollection
    },
    updateLocalFile: (newFile, existingFile) => {
      const result = localFileCollection.filter((file) => {
        return file._id !== existingFile._id
      })
      const updatedFileCollection = [
        ...result,
        documentInformationMapper(newFile, existingFile.customerID, existingFile.documentType),
      ]
      setLocalFileCollection(() => updatedFileCollection)
      return [...updatedFileCollection]
    },
    deleteFile: (fileKey: string) => {
      const result = localFileCollection.filter((file) => {
        return file._id !== fileKey
      })
      setLocalFileCollection(() => [...result])
      return result
    },
    uploadDocumentMutation: useMutation({
      mutationFn: ({ file, json, manualQuoteID }: ManualQuoteDocumentUploadVariables) =>
        uploadStore.documentServiceV2.uploadFile(file, 'manual-quote', manualQuoteID, JSON.stringify(json)),
      onError: handleNonStandardErrors,
    }),
  }
}
